import { NgModule } from '@angular/core';
import { RouterModule, Routes, withViewTransitions } from '@angular/router';
import { NotAllowedComponent } from './shared/components/not-allowed/not-allowed.component';
import { NotFoundComponent } from './shared/components/not-found/not-found.component';
import { LoginComponent } from './guest/login/login.component';
import { PageComponent } from './page/page.component';
import { ForgotPasswordComponent } from './guest/forgot-password/forgot-password.component';
import { CheckYourEmailComponent } from './guest/check-your-email/check-your-email.component';
import { ResetPasswordComponent } from './guest/reset-password/reset-password.component';
import { SetPasswordComponent } from './guest/set-password/set-password.component';
import { ChangePasswordComponent } from './guest/change-password/change-password.component';
import { ModuleLandingComponent } from './shared/components/landing/module-landing/module-landing.component';
import { AfterLoginGuard } from './guards/after-login.guard';
import { GuestMainComponent } from './guest/guest-main-copy/guest-main.component';
import { environment } from '../environments/environment';
import { AppBuild } from './common/interfaces/global-interfaces';
import { ExUserGuard } from './guards/exUser.guard';
import { InUserGuard } from './guards/inUser.guard';
import { BeforeLoginGuard } from './guards/before-login.guard';
import { DgLandingComponent } from './shared/components/landing/dg-landing/dg-landing.component';

const routes: Routes = [
  {
    path: '',
    component: GuestMainComponent,
    canActivate: [BeforeLoginGuard],
  },
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [BeforeLoginGuard],
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
    //canActivate: [BeforeLoginGuard]
  },
  {
    path: 'check-your-email',
    component: CheckYourEmailComponent,
    //canActivate: [BeforeLoginGuard]
  },
  {
    path: 'change-password',
    component: ChangePasswordComponent,
  },
  {
    path: 'reset-password/:token',
    component: ResetPasswordComponent,
    //canActivate: [BeforeLoginGuard]
  },
  {
    path: 'activate-account/:token',
    component: SetPasswordComponent,
    //canActivate: [BeforeLoginGuard]
  },
  {
    path: 'landing',
    component: ModuleLandingComponent,
    canActivate: [AfterLoginGuard],
  },
  {
    path: 'dg-landing',
    component:DgLandingComponent,
    canActivate: [AfterLoginGuard, InUserGuard],
  },
];

const inRoutes: Routes = [
  ...routes,
  {
    path: 'manage',
    component: PageComponent,
    canActivate: [AfterLoginGuard, InUserGuard],
    children: [
      {
        path: 'ict-services',
        loadChildren: () =>
          import('./modules/internal/ict-services/ict-services.module').then(
            (m) => m.IctServicesModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./modules/internal/general-settings/general-settings.module').then(
            (m) => m.GeneralSettingsModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'users-management',
        loadChildren: () =>
          import('./modules/internal/users-management/users-management.module').then(
            (m) => m.UsersManagementModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'projects-initiations',
        loadChildren: () =>
          import('./modules/internal/projects/projects.module').then(
            (m) => m.ProjectsModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'annual-inst-assessments',
        loadChildren: () =>
          import('./modules/internal/annual-assessment/annual-assessment.module').then(
            (m) => m.AnnualAssessmentModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'outreach',
        loadChildren: () =>
          import('./modules/internal/outreach/outreach.module').then(
            (m) => m.OutreachModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'document-management',
        loadChildren: () =>
          import('./modules/internal/document-management/document-management.module').then(
            (m) => m.DocumentManagementModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'asset-management',
        loadChildren: () =>
          import('./modules/internal/asset-management/asset-management.module').then(
            (m) => m.AssetManagementModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'assessments',
        loadChildren: () =>
          import('./modules/internal/inst-assessments/inst-assessments.module').then(
            (m) => m.InstAssessmentsModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'spoc-management',
        loadChildren: () =>
          import('./modules/internal/spoc-management/spoc-management.module').then(
            (m) => m.SpocManagementModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'security-mngt',
        loadChildren: () =>
          import('./modules/internal/security-management/security-management.module').then(
            (m) => m.SecurityManagementModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'quarterly-report',
        loadChildren: () =>
          import('./modules/internal/quarterly-report/quarterly-report.module').then(
            (m) => m.QuarterlyReportModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'assignments',
        loadChildren: () =>
          import('./modules/internal/assignments/assignments.module').then(
            (m) => m.AssignmentsModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'billing',
        loadChildren: () =>
          import('./modules/internal/billing/billing.module').then(
            (m) => m.BillingModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
      {
        path: 'projects-management',
        loadChildren: () =>
          import('./modules/internal/project-managements/project-managements.module').then(
            (m) => m.ProjectManagementsModule,
          ),
        canActivate: [AfterLoginGuard, InUserGuard],
      },
    ],
  },
  {
    path: 'not-allowed',
    component: NotAllowedComponent,
    canActivate: [AfterLoginGuard],
  },
  { path: '**', component: NotFoundComponent },
];

const exRoutes: Routes = [
  ...routes,
  {
    path: 'portal',
    component: PageComponent,
    canActivate: [AfterLoginGuard, ExUserGuard],
    children: [
      {
        path: 'ex-projects',
        loadChildren: () =>
          import('./modules/external/ex-projects/ex-projects.module').then(
            (m) => m.ExProjectsModule,
          ),
        canActivate: [AfterLoginGuard, ExUserGuard],
      },
      {
        path: 'ex-assessments',
        loadChildren: () =>
          import('./modules/external/ex-inst-assessments/ex-inst-assessments.module').then(
            (m) => m.ExInstAssessmentsModule,
          ),
        canActivate: [AfterLoginGuard, ExUserGuard],
      },
      {
        path: 'ex-settings',
        loadChildren: () =>
          import('./modules/external/ex-general-settings/ex-general-settings.module').then(
            (m) => m.ExGeneralSettingsModule,
          ),
        canActivate: [AfterLoginGuard, ExUserGuard],
      },
      {
        path: 'ex-outreach',
        loadChildren: () =>
          import('./modules/external/ex-outreach/ex-outreach.module').then(
            (m) => m.ExOutreachModule,
          ),
        canActivate: [AfterLoginGuard, ExUserGuard],
      },
      {
        path: 'ex-asset-management',
        loadChildren: () =>
          import('./modules/external/ex-asset-management/ex-asset-management.module').then(
            (m) => m.ExAssetManagementModule,
          ),
        canActivate: [AfterLoginGuard, ExUserGuard],
      },
      {
        path: 'ex-spoc-management',
        loadChildren: () =>
          import('./modules/external/ex-spoc-management/ex-spoc-management.module').then(
            (m) => m.ExSpocManagementModule,
          ),
        canActivate: [AfterLoginGuard, ExUserGuard],
      },
      {
        path: 'ex-document-management',
        loadChildren: () =>
          import('./modules/external/ex-document-management/ex-document-management.module').then(
            (m) => m.ExDocumentManagementModule,
          ),
        canActivate: [AfterLoginGuard, ExUserGuard],
      },
      {
        path: 'ex-ict-services',
        loadChildren: () =>
          import('./modules/external/ex-ict-services/ex-ict-services.module').then(
            (m) => m.ExIctServicesModule,
          ),
        canActivate: [AfterLoginGuard, ExUserGuard],
      },
      {
        path: 'ex-quarterly-report',
        loadChildren: () =>
          import('./modules/external/ex-quarterly-report/ex-quarterly-report.module').then(
            (m) => m.ExQuarterlyReportModule,
          ),
        canActivate: [AfterLoginGuard, ExUserGuard],
      },
      {
        path: 'ex-security-management',
        loadChildren: () =>
          import('./modules/external/ex-security-management/ex-security-management.module').then(
            (m) => m.ExSecurityManagementModule,
          ),
        canActivate: [AfterLoginGuard, ExUserGuard],
      },
    ],
  },
  {
    path: 'not-allowed',
    component: NotAllowedComponent,
    canActivate: [AfterLoginGuard],
  },
  { path: '**', component: NotFoundComponent },
];

@NgModule({
  imports: [
    RouterModule.forRoot(
      environment.SYSTEM_BUILD === AppBuild.INTERNAL ? inRoutes : exRoutes,
      { anchorScrolling: 'enabled' },
    ),
  ],
  exports: [RouterModule],
})
// environment.SYSTEM_BUILD === AppBuild.INTERNAL ? inRoutes : exRoutes,
// exRoutes,
// inRoutes,
export class AppRoutingModule {}
